import {
  Avatar,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import Upload01Icon from "@untitled-ui/icons-react/build/esm/Upload01";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import React, { useMemo, type FC } from "react";
import {
  DropEvent,
  FileRejection,
  useDropzone,
  type DropzoneOptions,
  type FileWithPath,
} from "react-dropzone";

import { bytesToSize } from "@zolo/lib";

import toast from "react-hot-toast";
import { FileIcon } from "./file-icon";

export type File = FileWithPath;

interface FileDropzoneProps extends DropzoneOptions {
  caption?: string;
  imageUploader?: boolean;
  files?: File[];
  onRemove?: (file: File) => void;
  onRemoveAll?: () => void;
  onUpload?: () => void;
  singleFile?: boolean;
  location?: boolean;
  hideUploadBtn?: boolean;
  disabled?: boolean;
  handleClick?: (file: File) => void;
  maxListHeight?: number;
}

export const FileDropzone: FC<FileDropzoneProps> = (props) => {
  const {
    caption,
    imageUploader,
    files = [],
    onRemove,
    onRemoveAll,
    onUpload,
    location,
    singleFile,
    hideUploadBtn = false,
    disabled = false,
    handleClick,
    maxListHeight,
    onDrop,
    maxSize,
    ...other
  } = props;
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      ...other,
      onDrop: (
        acceptedFiles: File[],
        fileRejections: FileRejection[],
        e: DropEvent,
      ) => {
        onDrop?.(acceptedFiles, fileRejections, e);
        if (
          fileRejections.length > 0 &&
          maxSize &&
          fileRejections.some((fileRef) => fileRef.file.size > maxSize)
        )
          toast.error("File size exceeds 2 MB limit");
      },
      maxSize,
    });
  const clickable = useMemo(() => Boolean(handleClick), [handleClick]);
  const hasAnyFiles = files.length > 0;

  return (
    <div>
      {(files?.length && singleFile) ||
      (files?.length && imageUploader) ? null : (
        <Box
          sx={{
            alignItems: "center",
            border: 1,
            borderRadius: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            outline: "none",
            p: 6,
            ...(isDragActive && {
              backgroundColor: "action.active",
              opacity: 0.5,
            }),
            "&:hover": {
              backgroundColor: "action.hover",
              cursor: "pointer",
              opacity: 0.5,
            },
          }}
          {...getRootProps()}
        >
          <input id="imageUloader" {...getInputProps()} />
          <Stack alignItems="center" direction="row" spacing={2}>
            <Avatar
              sx={{
                height: 64,
                width: 64,
              }}
            >
              <SvgIcon>
                <Upload01Icon />
              </SvgIcon>
            </Avatar>
            <Stack spacing={1}>
              <Typography
                sx={{
                  "& span": {
                    textDecoration: "underline",
                  },
                }}
                variant="h6"
              >
                <span>Click to upload {location && "Qr"}</span>{" "}
                {!location && "or drag and drop"}
              </Typography>
              {maxSize && (
                <Typography color="text.secondary" variant="body2">
                  Maximum file size allowed is {bytesToSize(maxSize)}
                </Typography>
              )}
              {caption && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  style={{
                    marginTop: 0,
                  }}
                >
                  {caption}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      )}
      {hasAnyFiles && (
        <Box
          sx={{
            mt: 2,
            ...(maxListHeight && {
              maxHeight: maxListHeight,
              overflowY: "scroll",
            }),
          }}
        >
          <List>
            {files.map((file) => {
              const extension = file.name.split(".").pop();

              return (
                <ListItem
                  key={file.path}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "& + &": {
                      mt: 1,
                    },
                    ...(clickable && {
                      "&:hover": {
                        backgroundColor: "action.hover",
                        cursor: "pointer",
                        opacity: 0.7,
                      },
                    }),
                  }}
                >
                  <ListItemIcon onClick={() => handleClick?.(file)}>
                    <FileIcon extension={extension} />
                  </ListItemIcon>
                  <ListItemText
                    onClick={() => handleClick?.(file)}
                    primary={file.name}
                    primaryTypographyProps={{
                      variant: "subtitle2",
                      style: { wordBreak: "break-word" },
                    }}
                    secondary={bytesToSize(file.size)}
                  />
                  {!disabled && (
                    <Tooltip title="Remove">
                      <IconButton edge="end" onClick={() => onRemove?.(file)}>
                        <SvgIcon>
                          <XIcon />
                        </SvgIcon>
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
              );
            })}
          </List>
          {!imageUploader && (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="flex-end"
              spacing={2}
              sx={{ mt: 2 }}
            >
              {singleFile ? null : (
                <Button
                  color="inherit"
                  onClick={onRemoveAll}
                  size="small"
                  type="button"
                >
                  Remove All
                </Button>
              )}
              {!hideUploadBtn && (
                <Button
                  onClick={onUpload}
                  size="small"
                  type="button"
                  variant="contained"
                >
                  Upload
                </Button>
              )}
            </Stack>
          )}
        </Box>
      )}
    </div>
  );
};